import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { Button, Col, Form, Row, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from "react-toastify";
import paths from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { useTranslation } from "react-i18next";
import { jwtDecode } from 'jwt-decode';
import { useAppContext } from '../../providers/appProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faShieldAlt, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import * as yup from "yup";
import { USER_ROLES } from "config/roles.config";
import { getDomainConfig } from "../../config/domainConfig";

const AuthBlockReason = Object.freeze({
  ACCOUNT_DEACTIVATED: "account_deactivated",
  ACCOUNT_OFFBOARDED: "account_offboarded",
  PAYMENT_REQUIRED: "payment_required",
  SUBSCRIPTION_EXPIRED: "subscription_expired",
});

const BLOCKING_MESSAGES = Object.freeze({
  [AuthBlockReason.ACCOUNT_DEACTIVATED]: "accountDeactivated",
  [AuthBlockReason.ACCOUNT_OFFBOARDED]: "accountOffboardedRestricted",
  [AuthBlockReason.PAYMENT_REQUIRED]: "paymentRequiredEmployeeBlocked",
  [AuthBlockReason.SUBSCRIPTION_EXPIRED]: "subscriptionExpiredEmployeeBlocked",
});

const LOGIN_BLOCKING_MESSAGES = Object.freeze({
  ACCOUNT_DEACTIVATED: "accountDeactivated",
  ACCOUNT_OFFBOARDED: "accountOffboardedRestricted",
  PAYMENT_REQUIRED_EMPLOYEE_BLOCKED: "paymentRequiredEmployeeBlocked",
  SUBSCRIPTION_EXPIRED_EMPLOYEE_BLOCKED: "subscriptionExpiredEmployeeBlocked",
});

const LoginForm = ({ hasLabel = false, onCodeSent }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: ['', '', '', '', '', ''],
    remember: false
  });

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setConfig } = useAppContext();
  const culture = localStorage.getItem("culture") || "en";
  const signupUrl = process.env.REACT_APP_SIGNUP_URL;
  const orgName = process.env.REACT_APP_ORGANIZATION_NAME;
  const secureLoginLabels = getDomainConfig().secureLoginLabels;
  const [activeTab, setActiveTab] = useState('secureCode');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(900);
  const timerRef = useRef(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const emailSchema = yup.object({
    email: yup
      .string()
      .email(t("invalidEmail"))
      .required(t("emailRequired")),
  });

  const startTimer = () => {
    clearInterval(timerRef.current);
    setSecondsLeft(900);
    timerRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (isCodeSent) {
      setTimeout(() => {
        document.getElementById('otp-0')?.focus();
      }, 100);
    }
  }, [isCodeSent]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const formatTimer = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // Email masking utility
  const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return email;
    return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
  };

  const handleUserStatusCheck = (user) => {
    if (user.IsDelete === true || user.Status === "1") {
      const message = user.IsDelete ? t("userDeletedMessage") : t("userInactiveMessage");
      toast.error(message, { position: "bottom-left", autoClose: 5000 });
      setFormData({ email: "", password: "", otp: ['', '', '', '', '', ''], remember: false });
      return true; // user is blocked
    }
    return false; // user is OK
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      const email = e.target.email.value;
      const password = e.target.password.value;

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/userRoute/login`, {
        email,
        password,
      },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const token = response.data.token;
      // const refreshToken = response.data.refreshToken;
      const user = response.data.user;
      const emailConfigValid = response.data.emailConfigValid;

      // const decodedToken = jwtDecode(token);
      // const tokenExpiry = decodedToken.exp;
      // sessionStorage.setItem('tokenExpiry', tokenExpiry);


      // sessionStorage.setItem("token", token);
      // // sessionStorage.setItem("refreshToken", refreshToken);
      // sessionStorage.setItem("isLoggedIn", "true");
      // sessionStorage.setItem("UserId", user.UserId);
      // sessionStorage.setItem("UserName", user.Name);
      // sessionStorage.setItem("UserEmail", user.Emailaddress);
      // sessionStorage.setItem("LocationId", user.LocationId);
      sessionStorage.removeItem("app_update_required");

      if (handleUserStatusCheck(user)) return;

      const rolesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const roles = rolesResponse.data;

      const employeeRole = roles.find(role => role.Name.toLowerCase() === USER_ROLES.EMPLOYEE.toLowerCase());

      const isMedewerker = user.Role === employeeRole?.RoleId;

      const missingSignature = !user.CompanySignature && !isMedewerker;

      // sessionStorage.setItem("isMedewerker", isMedewerker ? "true" : "false"); // store for later checks
      // sessionStorage.setItem("missingSignature", missingSignature ? "true" : "false");

      const missingEmailConfig = !user.SenderEmail;
      // sessionStorage.setItem("missingEmailConfig", missingEmailConfig);
      const userRole = roles.find(role => role.RoleId === user.Role);
      // sessionStorage.setItem("UserRole", userRole.Name);

      toast.success(`${t("loginSuccess")} ${email}`, { position: "bottom-left", autoClose: 5000 });

      if (!emailConfigValid && emailConfigValid !== undefined && !isMedewerker) {
        localStorage.setItem("emailConfigInvalid", "true");
      }

      const locationIds = Array.isArray(user.locations)
        ? user.locations.map(l => l.LocationId)
        : [];
      const defaultLocationId = user.LastLoginLocation || locationIds[0] || null;

      const culture = user.DefaultLanguage?.toLowerCase() || 'nl';
      // persist
      localStorage.setItem('culture', culture);
      localStorage.setItem('i18nextLng', culture);

      i18n.changeLanguage(culture);

      // update app-wide language state
      setConfig('language', culture);
      localStorage.setItem("token", token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("UserId", user.UserId);
      localStorage.setItem("UserName", user.Name);
      localStorage.setItem("UserEmail", user.Emailaddress);
      localStorage.setItem("LocationId", defaultLocationId);
      localStorage.setItem("UserRole", userRole.Name);
      localStorage.setItem("missingSignature", missingSignature ? "true" : "false");
      localStorage.setItem("isMedewerker", isMedewerker ? "true" : "false");
      localStorage.setItem("missingEmailConfig", missingEmailConfig);
      localStorage.setItem("customerId", user.CustomerId);
      localStorage.setItem("hasCompletedTourGuide", user.HasCompletedTourGuide ? "true" : "false");


      // navigate("/dashboard")
      const redirectPath = userRole.Name?.toLowerCase() === USER_ROLES.CLIENT.toLowerCase()
        ? "/client/dashboard"
        : (isMedewerker ? "/employee/dashboard" : "/dashboard");

      if (activeTab === 'secureCode') {
        setLoginSuccess(true);
        setTimeout(() => navigate(redirectPath), 3000);
      } else {
        navigate(redirectPath);
      }
    } catch (error) {
      const backendCode = error?.response?.data?.message;

      if (error?.response?.status === 403 && LOGIN_BLOCKING_MESSAGES[backendCode]) {
        toast.error(t(LOGIN_BLOCKING_MESSAGES[backendCode]), { position: "top-right", autoClose: 5000 });
        setFormData({ email: "", password: "", otp: ['', '', '', '', '', ''], remember: false });
        return;
      }

      toast.error(t("loginErrorMessage"), { position: "top-right", autoClose: 5000 });
      setFormData({ email: "", password: "", otp: ['', '', '', '', '', ''], remember: false });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const getOtpErrorMessage = () => {
    if (!otpError) return null;

    const { reason, backendMessage } = otpError;

    const reasonMessages = {
      incomplete: t("otpErrorIncomplete"),
      expired: t("otpErrorExpired"),
      invalid: t("otpErrorInvalid"),
      already_used: t("otpErrorAlreadyUsed"),
      email_mismatch: t("otpErrorEmailMismatch"),
    };

    return reasonMessages[reason] || backendMessage || t("otpErrorGeneric");
  };

  const handleSendSecureCode = async () => {
    setEmailError('');

    try {
      await emailSchema.validate({ email: formData.email });
    } catch (validationError) {
      setEmailError(validationError.message);
      return;
    }

    try {
      setIsSendingOtp(true);

      const checkRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/checkEmail/${formData.email}`,
        {
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
        }
      );

      if (!checkRes.data.exists) {
        toast.error(t("userNotFoundMessage"), { position: "bottom-left", autoClose: 5000 });
        return;
      }

      const culture = localStorage.getItem("culture") || "en";

      await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendLoginOtp`,
        { email: formData.email, culture },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      setIsCodeSent(true);
      setOtpError(null);
      if (onCodeSent) onCodeSent(true);
      startTimer();
    } catch {
      toast.error(t("loginErrorMessage"), { position: "bottom-left", autoClose: 5000 });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const culture = localStorage.getItem("culture") || "en";

      await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendLoginOtp`,
        { email: formData.email, culture },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      setFormData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));
      setOtpError(null);
      startTimer();

      toast.success(t(secureLoginLabels.resendToast), { position: "bottom-left", autoClose: 3000 });
      setTimeout(() => document.getElementById('otp-0')?.focus(), 100);

    } catch {
      toast.error(t("loginErrorMessage"), { position: "bottom-left", autoClose: 5000 });
    }
  };

  useEffect(() => {
    const handleBackButton = () => {
      const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
      if (!isLoggedIn) {
        navigate(paths.login, { replace: true });
      }
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  useEffect(() => {
    const savedLang = localStorage.getItem('culture') || 'en';
    i18n.changeLanguage(savedLang);
    setConfig('language', savedLang);
  }, []);

  const handleFieldChange = async e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'email') {
      try {
        await emailSchema.validate({ email: value });
        setEmailError('');
      } catch (validationError) {
        setEmailError(validationError.message);
      }
    }
  };

  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...formData.otp];
    newOtp[index] = value.substring(value.length - 1);
    setFormData(prev => ({ ...prev, otp: newOtp }));
    setOtpError(null);  // already there — no change needed
    if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0)
      document.getElementById(`otp-${index - 1}`)?.focus();
  };

  const handleOtpPaste = (e, index) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = [...formData.otp];
    for (let i = 0; i < pasted.length; i++) {
      if (index + i < 6) newOtp[index + i] = pasted[i];
    }
    setFormData(prev => ({ ...prev, otp: newOtp }));
    setOtpError(null);
    const nextFocus = Math.min(index + pasted.length, 5);
    setTimeout(() => document.getElementById(`otp-${nextFocus}`)?.focus(), 0);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    const otpValue = formData.otp.join('');

    if (otpValue.length < 6) {
      setOtpError("incomplete");
      return;
    }

    if (secondsLeft === 0) {
      setOtpError("expired");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setOtpError(null);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/verifyLoginOtp`,
        { email: formData.email, code: otpValue },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      const { token, user } = response.data;

      if (handleUserStatusCheck(user)) return;

      const rolesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/roleRoute/roles`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      const roles = rolesResponse.data;
      const userRole = roles.find(r => r.RoleId === user.Role);

      const employeeRole = roles.find(r => r.Name.toLowerCase() === USER_ROLES.EMPLOYEE.toLowerCase());
      const isMedewerker = user.Role === employeeRole?.RoleId;
      const missingSignature = !user.CompanySignature && !isMedewerker;
      const missingEmailConfig = !user.SenderEmail;
      const locationIds = Array.isArray(user.locations) ? user.locations.map(l => l.LocationId) : [];
      const defaultLocationId = user.LastLoginLocation || locationIds[0] || null;
      const userCulture = user.DefaultLanguage?.toLowerCase() || 'nl';

      // ── Persist to localStorage ───────────────────────────────────────
      localStorage.setItem('culture', userCulture);
      localStorage.setItem('i18nextLng', userCulture);
      i18n.changeLanguage(userCulture);
      setConfig('language', userCulture);
      localStorage.setItem("token", token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("UserId", user.UserId);
      localStorage.setItem("UserName", user.Name);
      localStorage.setItem("UserEmail", user.Emailaddress);
      localStorage.setItem("LocationId", defaultLocationId);
      localStorage.setItem("UserRole", userRole.Name);
      localStorage.setItem("missingSignature", missingSignature ? "true" : "false");
      localStorage.setItem("isMedewerker", isMedewerker ? "true" : "false");
      localStorage.setItem("missingEmailConfig", missingEmailConfig);
      localStorage.setItem("customerId", user.CustomerId);
      sessionStorage.removeItem("app_update_required");
      localStorage.setItem("hasCompletedTourGuide", user.HasCompletedTourGuide ? "true" : "false");

      const redirectPath = userRole.Name?.toLowerCase() === USER_ROLES.CLIENT.toLowerCase()
        ? "/client/dashboard"
        : (isMedewerker ? "/employee/dashboard" : "/dashboard");

      setLoginSuccess(true);
      setTimeout(() => {
        navigate(redirectPath);
      }, 3000);

    } catch (err) {
      const reason = err?.response?.data?.reason || "invalid";
      const backendMessage = err?.response?.data?.message || null;

      const blockingMessage = BLOCKING_MESSAGES[reason];
      if (blockingMessage) {
        toast.error(t(blockingMessage), { position: "top-right", autoClose: 5000 });
        setFormData({ email: "", password: "", otp: ['', '', '', '', '', ''], remember: false });
        setIsCodeSent(false);
        if (onCodeSent) onCodeSent(false);
        return;
      }

      setOtpError({ reason, backendMessage });
      setFormData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));
      setTimeout(() => document.getElementById('otp-0')?.focus(), 50);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  if (loginSuccess) {
    return (
      <div className="text-center py-4 px-3">
        <div className="success-hero-area mb-4">

          <div className="confetti-arc left"></div>
          <div className="confetti-arc right"></div>

          <div className="outer-circle mx-auto">
            <div className="inner-circle shadow-sm">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="fw-bold text-dark mb-1">{t("loginSuccessTitle")}</h3>
          <p className=" mt-4 text-muted small">{t("loginSuccessSubtitle")}{process.env.REACT_APP_ORGANIZATION_NAME}</p>
        </div>

        <div className="secure-info-pill d-inline-flex align-items-center gap-2 px-4 py-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00c853" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
          <span className="small fw-medium text-success">{t("loginSuccessInfo")}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isCodeSent && (
        <div className="bg-light p-1 rounded-3 mb-4 d-flex">
          <div className="flex-fill text-center">
            <button
              type="button"
              onClick={() => { 
                setActiveTab("secureCode"); 
                setIsCodeSent(false); 
                setFormData(prev => ({...prev, otp:['','','','','',''] }));
                setOtpError(null);
              }}
              className={`btn w-100 d-flex align-items-center justify-content-center py-2 shadow-none border-0 ${activeTab === "secureCode"
                ? "bg-primary-subtle text-primary fw-bold border-bottom border-primary border-3 rounded-top-2 rounded-bottom-0"
                : "text-secondary rounded-2"
                }`}
            >
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              {t(secureLoginLabels.tab)}
            </button>
          </div>

          <div className="flex-fill text-center">
            <button
              type="button"
              onClick={() => { 
                setActiveTab("password"); 
                setIsCodeSent(false); 
                setFormData(prev => ({...prev, otp:['','','','','',''] }));
                setOtpError(null);
              }}
              className={`btn w-100 d-flex align-items-center justify-content-center py-2 shadow-none border-0 ${activeTab === "password"
                ? "bg-primary-subtle text-primary fw-bold border-bottom border-primary border-3 rounded-top-2 rounded-bottom-0"
                : "text-secondary rounded-2"
                }`}
            >
              <FontAwesomeIcon icon={faLock} className="me-2" />
              {t("passwordLogin")}
            </button>
          </div>
        </div>
      )}

      {!isCodeSent ? (
        <Form noValidate onSubmit={activeTab === 'password' ? handleSubmit : (e) => e.preventDefault()}>

          {activeTab === 'secureCode' && (
            <div className="text-center mb-4 px-3">
              <p className="small fs-10 opacity-75">
                {t("LoginWithoutPassword")} <br />
                {t(secureLoginLabels.subtitle)}
              </p>
            </div>
          )}

          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>{t("emailAddress")}</Form.Label>}
            <Form.Control
              type="email"
              name="email"
              placeholder={t("user@mail.com")}
              value={formData.email}
              onChange={handleFieldChange}
              isInvalid={!!emailError}
              required
            />
            <Form.Control.Feedback type="invalid">
              {emailError}
            </Form.Control.Feedback>
          </Form.Group>

          {activeTab === 'password' ? (
            <>
              <Form.Group className="mb-3">
                {hasLabel && <Form.Label>{t("password")}</Form.Label>}
                <Form.Control
                  type="password"
                  name="password"
                  placeholder={t("*********")}
                  value={formData.password}
                  onChange={handleFieldChange}
                  required
                />
              </Form.Group>
              <Row className="justify-content-between align-items-center">
                <Col xs="auto">
                  <Link className="fs-10 mb-0" to={paths.forgotPassword}>
                    {t("forgetPassword")}
                  </Link>
                </Col>
              </Row>
            </>
          ) : null}

          <Button
            type={activeTab === 'password' ? "submit" : "button"}
            className="mt-3 w-100 d-flex align-items-center justify-content-center"
            onClick={activeTab === 'secureCode' ? handleSendSecureCode : undefined}
            disabled={
              isSendingOtp || isLoggingIn ||
              !formData.email || 
              (activeTab === 'password' && !formData.password)
            }
          >
            {activeTab === 'secureCode' && isSendingOtp
              ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  {t("sendingCode")}
                </>
              )
              : activeTab === 'password' && isLoggingIn
                ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {t("loggingIn")}
                  </>
                )
                : activeTab === 'password' ? t("loginButton") : t(secureLoginLabels.sendButton)
            }
          </Button>

          {activeTab === 'secureCode' && (
            <div className="d-flex align-items-center justify-content-center mt-3 text-muted">
              <FontAwesomeIcon
                icon={faShieldAlt}
                className="me-2 text-primary text-opacity-50 small"
              />
              <span className="small fs-10 opacity-75">
                {t(secureLoginLabels.hint)}
              </span>
            </div>
          )}
        </Form>

      ) : (

        <Form noValidate onSubmit={handleOtpSubmit}>
          <div className="text-center">
            <h3 className="fw-bold mb-2">{t(secureLoginLabels.heading)}</h3>
            <p className="text-muted fs-10 mb-4">{t(secureLoginLabels.sentTo)} <strong>{maskEmail(formData.email)}</strong></p>

            <div className="d-flex justify-content-center gap-2 mb-4">
              {formData.otp.map((data, index) => (
                <Form.Control
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  className="text-center p-0 otp-input"
                  maxLength={1}
                  value={data}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => handleOtpPaste(e, index)}
                  required
                />
              ))}
            </div>

            <p className="fs-10 text-muted mb-4">
              <i className="far fa-clock me-1"></i> {t('codeExpiresIn')}{' '}
              {secondsLeft > 0
                ? <span className="text-primary fw-bold">{formatTimer(secondsLeft)}</span>
                : <span className="text-danger fw-bold">{t('codeExpired')}</span>
              }
            </p>

            {otpError && (
              <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2 text-start small">
                <FontAwesomeIcon icon={faExclamationCircle} className="flex-shrink-0" />
                <span>{getOtpErrorMessage()}</span>
              </div>
            )}

            <Button type="submit" variant="primary" className="w-100 py-2 mb-3 d-flex align-items-center justify-content-center" disabled={isVerifyingOtp || formData.otp.some(data => data === '')}>
              {isVerifyingOtp
                ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("verifying")}
                  </>
                )
                : t("loginButton")
              }
            </Button>

            <div className="d-flex justify-content-between">
              <Button
                variant="link"
                className="fs-10 p-0 text-decoration-none"
                onClick={handleResendCode}
              >
                {t("resendCode")}
              </Button>
              <Button
                variant="link"
                className="fs-10 p-0 text-decoration-none"
                onClick={() => {
                  setIsCodeSent(false);
                  if (onCodeSent) onCodeSent(false);
                }}
              >
                {t("changeEmail")}
              </Button>
            </div>
          </div>
        </Form>

      )}
    </>
  );
};

LoginForm.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool
};

export default LoginForm;